import React, {FC} from 'react';
import {connect} from 'react-redux';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {VisualWorkspaceTeamData} from '../../../../models/api/visualWorkspaceModel';
import {AppState} from '../../../../store/appState';
import {getTeams} from '../../../../store/data/teams/teamsSelectors';
import {LeaveSandboxCoordinator} from '../../../onboarding/sandbox/leaveSandboxCoordinator';
import {TeamWorkspaceContainer} from './teamWorkspaceContainer';

/*
 * Props.
 */

export interface MaybeTeamWorkspaceContainerProps {
  /** Teams data passed from VisualWorkspaceModel */
  visualWorkspaceTeam: VisualWorkspaceTeamData;
  panelId: InboxesPanelId;
  /** Whether this is the first team. */
  isFirstTeam: boolean;
}

interface MaybeTeamWorkspaceContainerComponentProps {
  team: TeamModel | undefined;
  /** Teams data passed from VisualWorkspaceModel */
  visualWorkspaceTeam: VisualWorkspaceTeamData;
  panelId: InboxesPanelId;
  /** Whether this is the first team. */
  isFirstTeam: boolean;
}

/*
 * Selectors.
 */

function getTeamId(_state: AppState, props: MaybeTeamWorkspaceContainerProps) {
  return props.visualWorkspaceTeam.teamId;
}

function makeGetTeam() {
  return createSelector([getTeams, getTeamId], (teams, id) => teams[id]);
}

/*
 * Component.
 */

const hideSandboxLabel = <Trans id="workspaces-sandbox-remove">Leave sandbox workspace</Trans>;

const MaybeTeamWorkspaceContainerComponent: FC<MaybeTeamWorkspaceContainerComponentProps> = (props) => {
  const {team, visualWorkspaceTeam, panelId, isFirstTeam} = props;

  if (!team) {
    return null;
  }

  if (team.isSandbox) {
    return (
      <LeaveSandboxCoordinator>
        {({onLeaveSandboxClick}) => (
          <TeamWorkspaceContainer
            team={team}
            visualWorkspaceTeam={visualWorkspaceTeam}
            panelId={panelId}
            isFirstTeam={isFirstTeam}
            isNotReorderable
            hideSectionLabel={hideSandboxLabel}
            onHideSection={() => {
              onLeaveSandboxClick();
              trackInterfaceEvent(InterfaceEventsEnum.P0_LEAVE_SANDBOX_CLICK);
            }}
          />
        )}
      </LeaveSandboxCoordinator>
    );
  }

  return (
    <TeamWorkspaceContainer
      team={team}
      visualWorkspaceTeam={visualWorkspaceTeam}
      panelId={panelId}
      isFirstTeam={isFirstTeam}
      isNotReorderable={false}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MaybeTeamWorkspaceContainerProps>()(() => ({
  team: makeGetTeam(),
}));

export const MaybeTeamWorkspaceContainer = connect(mapStateToProps)(MaybeTeamWorkspaceContainerComponent);
